import { CommonModule } from '@angular/common';
import { Component, Inject, InjectionToken, OnDestroy, Optional, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { ContentGenerationService } from '@vendasta/email-builder';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BehaviorSubject, Observable } from 'rxjs';

export interface PromptBuilder {
  build(s: string): string;
}

export const PROMPT_BUILDER_INJECTION_TOKEN = new InjectionToken<PromptBuilder>(
  'com.vendasta.email_builder.suggest_content.prompt',
);

export class DefaultPromptBuilder implements PromptBuilder {
  build(prompt: string): string {
    return `Write a paragraph for an email based on the following prompt. Prompt: ${prompt}`;
  }
}

@Component({
  selector: 'email-suggest-content-dialog',
  templateUrl: './suggest-content-dialog.component.html',
  styleUrls: ['./suggest-content-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatInputModule,
    TranslateModule,
    GalaxyButtonLoadingIndicatorModule,
    GalaxyFormFieldModule,
  ],
})
export class SuggestContentComponent implements OnDestroy {
  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loading$: Observable<boolean> = this.loading$$.asObservable();

  promptText = '';
  contentText = '';
  subscriptions = [];

  @ViewChild('contentInput', { read: MatInputModule, static: true }) contentInput: MatInputModule;
  @ViewChild('promptInput', { read: MatInputModule, static: true }) promptInput: MatInputModule;

  constructor(
    @Inject(ContentGenerationService) private readonly contentGenerationService: ContentGenerationService,
    public dialogRef: MatDialogRef<SuggestContentComponent, string | null>,
    private posthogService: ProductAnalyticsService,
    @Inject(PROMPT_BUILDER_INJECTION_TOKEN) @Optional() private readonly promptBuilder: PromptBuilder,
    private readonly snackbarService: SnackbarService,
  ) {}

  suggestMessage(prompt: string): void {
    this.posthogService.trackEvent('user-clicked-suggest-message', 'suggest-content', 'click', null, {
      prompt: prompt,
    });
    const promptBuilder = this.promptBuilder || new DefaultPromptBuilder();
    const promptTemplate = promptBuilder.build(prompt);
    this.loading$$.next(true);
    this.subscriptions.push(
      this.contentGenerationService.suggestContent(promptTemplate).subscribe({
        next: (suggestedContent) => {
          this.loading$$.next(false);
          this.contentText = suggestedContent;
        },
        error: () => {
          this.loading$$.next(false);
          this.snackbarService.openErrorSnack('EMAIL_BUILDER.CONTENT_SUGGESTION.ERROR_MESSAGE');
        },
      }),
    );
  }

  useSuggestedMessage(content: string): void {
    this.posthogService.trackEvent('user-used-suggested-message', 'suggest-content', 'click', null, {
      suggestedContent: content,
    });
    this.dialogRef.close(this.formatContent(content));
  }

  formatContent(content: string): string {
    return content.replace(/\r\n|\r|\n/g, '<br>');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  closeDialog(): void {
    this.dialogRef.close(null);
  }
}
